import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import Button from '@components/Button/Button';
import Select from '@components/Form/Select/Select';
import Input from '@components/Form/Input/Input';
import Icon from '@components/common/Icon';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import mentorsService from '@/services/mentors/mentorsService';
import evaluationsService, {
    type EvaluationRow,
    type GridQuery,
} from '@/services/evaluations/evaluationsService';
import EvaluationForm from './EvaluationForm';
import DownloadTemplateModal from './DownloadTemplateModal';
import ImportEvaluationModal from './ImportEvaluationModal';

interface Props {
    cohortId: string;
    cohortName?: string;
    hideSummary?: boolean;
}

const STATUS_VALUES: NonNullable<GridQuery['status']>[] = ['SUBMITTED', 'DRAFT', 'NOT_STARTED'];

/** A student's status is derived from how many judges have weighed in, not one evaluator's record. */
const rowStatus = (row: EvaluationRow): 'SUBMITTED' | 'DRAFT' | 'NOT_STARTED' =>
    row.judges_submitted > 0 ? 'SUBMITTED' : row.judges_total > 0 ? 'DRAFT' : 'NOT_STARTED';

const statusPill = (row: EvaluationRow, label: (k: string) => string) => {
    const s = rowStatus(row);
    const cls =
        s === 'NOT_STARTED'
            ? 'bg-[var(--primary-surface-color)] text-[var(--subtitle-color)]'
            : s === 'SUBMITTED'
                ? 'bg-green-50 text-green-700 border border-green-100'
                : 'bg-amber-50 text-amber-700 border border-amber-100';
    return (
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${cls}`}>
            {label(s)}
        </span>
    );
};

const EvaluationGrid: React.FC<Props> = ({ cohortId, cohortName, hideSummary }) => {
    const { t } = useTranslation('evaluation');
    const [activeEnrollmentId, setActiveEnrollmentId] = useState<string | null>(null);
    const [showDownload, setShowDownload] = useState(false);
    const [showImport, setShowImport] = useState(false);
    // Remembered across students in this session so a manual bulk-entry pass (one
    // judge, many students via prev/next) doesn't re-ask for the judge every time.
    const [lastJudgeId, setLastJudgeId] = useState('');

    const [mentorId, setMentorId] = useState('');
    const [status, setStatus] = useState<GridQuery['status'] | ''>('');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        setMentorId('');
    }, [cohortId]);

    const { data: mentorsRes } = useQuery({
        queryKey: ['evaluation-mentors'],
        queryFn: () => mentorsService.getMentors(1, 300),
        staleTime: 5 * 60 * 1000,
    });

    const mentorOptions = useMemo(
        () => [
            { value: '', label: t('filters.mentorAll') },
            ...(mentorsRes?.data?.data ?? []).map((m) => ({ value: m.id, label: m.name })),
        ],
        [mentorsRes, t],
    );

    const statusOptions = [
        { value: '', label: t('filters.statusAll') },
        ...STATUS_VALUES.map((s) => ({ value: s, label: t(`status.${s}`) })),
    ];

    const query = useQuery({
        queryKey: ['evaluation-grid', cohortId, mentorId, status ?? '', debouncedSearch],
        queryFn: () =>
            evaluationsService.getGrid({
                cohort_id: cohortId,
                mentor_id: mentorId || undefined,
                status: status || undefined,
                search: debouncedSearch || undefined,
            }),
        enabled: !!cohortId,
        placeholderData: (prev) => prev,
    });

    const rows = query.data?.rows ?? [];
    const summary = query.data?.summary;
    const passMark = query.data?.rubric.pass_mark ?? null;

    const activeIndex = useMemo(
        () => rows.findIndex((r) => r.enrollment_id === activeEnrollmentId),
        [rows, activeEnrollmentId],
    );

    const columns = useMemo<ColumnDef<EvaluationRow, unknown>[]>(
        () => [
            {
                accessorKey: 'enrollment_number',
                header: t('grid.enrolment'),
                size: 150,
                cell: ({ row }) => (
                    <span className="font-semibold text-[var(--secondary-color)]">
                        {row.original.enrollment_number}
                    </span>
                ),
            },
            {
                id: 'student',
                header: t('grid.student'),
                size: 240,
                cell: ({ row }) => (
                    <div>
                        <div className="text-[var(--secondary-color)] font-medium">{row.original.student.name}</div>
                        <div className="text-[10px] text-[var(--subtitle-color)]">{row.original.student.email}</div>
                    </div>
                ),
            },
            {
                id: 'mentor',
                header: t('grid.mentor'),
                size: 160,
                cell: ({ row }) => (
                    <span className="text-[var(--subtitle-color)]">{row.original.mentor?.name ?? '—'}</span>
                ),
            },
            {
                id: 'status',
                header: t('grid.status'),
                size: 150,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex flex-col items-center gap-0.5">
                        {statusPill(row.original, (k) => t(`status.${k}`))}
                        {row.original.judges_total > 0 && (
                            <span className="text-[10px] text-[var(--subtitle-color)]">
                                {t('grid.judgesCount', {
                                    submitted: row.original.judges_submitted,
                                    total: row.original.judges_total,
                                })}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                id: 'score',
                header: t('grid.score'),
                size: 90,
                cell: ({ row }) => {
                    const { final } = row.original;
                    const score = final.judges_counted > 0 ? final.total_weighted : null;
                    const color =
                        score == null
                            ? 'text-[var(--subtitle-color)]'
                            : passMark != null && score < passMark
                                ? 'text-amber-600'
                                : 'text-[var(--secondary-color)]';
                    return <span className={`font-bold text-center block ${color}`}>{score == null ? '—' : score}</span>;
                },
            },
            {
                id: 'actions',
                header: t('grid.action') || ' ',
                size: 130,
                enableSorting: false,
                cell: ({ row }) => (
                    <Button
                        size="sm"
                        variant={row.original.judges_total > 0 ? 'outline' : 'primary'}
                        onClick={() => setActiveEnrollmentId(row.original.enrollment_id)}
                    >
                        {row.original.judges_total > 0 ? t('grid.view') : t('grid.evaluate')}
                    </Button>
                ),
            },
        ],
        [t, passMark],
    );

    if (!cohortId) {
        return (
            <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] py-16 text-center text-sm text-[var(--subtitle-color)]">
                {t('grid.noCohort')}
            </div>
        );
    }

    const searchIcon = (
        <button type="button" className="flex items-center cursor-pointer">
            <Icon name="search" className="w-3 2xl:w-[14px] h-auto" />
        </button>
    );

    return (
        <div className="flex flex-col gap-4 2xl:gap-5">
            {/* Toolbar card */}
            <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] p-3 2xl:p-5">
                <div className="flex items-center justify-end gap-2 mb-3">
                    <Button variant="outline" size="sm" onClick={() => setShowDownload(true)}>
                        {t('download.button')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                        {t('import.button')}
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 lg:gap-4 2xl:gap-5">
                    <Select
                        label={t('filters.mentor')}
                        options={mentorOptions}
                        value={mentorId}
                        onChange={(v) => setMentorId(v || '')}
                        isClearable={false}
                    />
                    <Select
                        label={t('filters.status')}
                        options={statusOptions}
                        value={status || ''}
                        onChange={(v) => setStatus((v || '') as GridQuery['status'] | '')}
                        isClearable={false}
                    />
                    <Input
                        label={t('filters.search')}
                        placeholder={t('filters.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        leftElement={searchIcon}
                    />
                </div>
            </div>

            {/* Summary */}
            {!hideSummary && summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 2xl:gap-5">
                    {(
                        [
                            ['total', summary.total, 'text-[var(--secondary-color)]'],
                            ['submitted', summary.submitted, 'text-green-700'],
                            ['draft', summary.draft, 'text-amber-700'],
                            ['not_started', summary.not_started, 'text-[var(--subtitle-color)]'],
                        ] as const
                    ).map(([key, value, color]) => (
                        <div
                            key={key}
                            className="rounded-2xl border border-[var(--neutral-color)] bg-[var(--white-color)] px-4 py-3"
                        >
                            <div className={`text-xl font-extrabold ${color}`}>{value}</div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--subtitle-color)]">
                                {t(`summary.${key}`)}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {query.isError ? (
                <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                    {t('grid.loadError')}
                </div>
            ) : (
                <DataGridTable
                    data={rows}
                    columns={columns}
                    loading={query.isLoading}
                    hidePagination
                    skeletonRows={6}
                />
            )}

            {activeEnrollmentId && (
                <EvaluationForm
                    key={activeEnrollmentId}
                    enrollmentId={activeEnrollmentId}
                    index={activeIndex}
                    total={rows.length}
                    defaultJudgeId={lastJudgeId}
                    onJudgeChange={setLastJudgeId}
                    onClose={() => setActiveEnrollmentId(null)}
                    onSaved={() => query.refetch()}
                    onPrev={
                        activeIndex > 0 ? () => setActiveEnrollmentId(rows[activeIndex - 1].enrollment_id) : undefined
                    }
                    onNext={
                        activeIndex >= 0 && activeIndex < rows.length - 1
                            ? () => setActiveEnrollmentId(rows[activeIndex + 1].enrollment_id)
                            : undefined
                    }
                    isPrevDisabled={activeIndex <= 0}
                    isNextDisabled={activeIndex < 0 || activeIndex >= rows.length - 1}
                />
            )}

            {showDownload && (
                <DownloadTemplateModal
                    isOpen
                    onClose={() => setShowDownload(false)}
                    cohortId={cohortId}
                    cohortName={cohortName}
                />
            )}

            {showImport && (
                <ImportEvaluationModal
                    isOpen
                    onClose={() => setShowImport(false)}
                    onImported={() => query.refetch()}
                    cohortId={cohortId}
                />
            )}
        </div>
    );
};

export default EvaluationGrid;
